import { memo, useEffect, useRef, useState } from "react";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { AlertTriangle, Bot, CalendarClock, Clock, EyeOff, Flag, GitBranch, GitFork, Hourglass, Play, Sparkles, Users, Workflow, XCircle, Zap } from "lucide-react";
import { cn } from "@ncp/ui";
import { NODE_W } from "../graph";
import type { GraphIssue, NodeKind, WfNode } from "../types";

export type DiffStatus = "added" | "modified" | "removed";

export interface BuilderNodeData extends Record<string, unknown> {
  node: WfNode;
  isStart: boolean;
  issues: GraphIssue[];
  diff?: DiffStatus;
  ghost?: boolean;
  heat?: number;
  heatMax?: number;
  sim?: "current" | "visited";
  locale: "en" | "ar";
  roleLabel: (r: string) => string;
  agentName: (k: string) => string;
  editable: boolean;
  onRename?: (id: string, label: string) => void;
}

export const KIND_META: Record<NodeKind, { icon: typeof Play; tone: string; chip: string; label: string }> = {
  Start: { icon: Play, tone: "border-s-success-500", chip: "bg-success-600 text-white", label: "Start" },
  Task: { icon: Users, tone: "border-s-sand-400", chip: "bg-sand-200 text-sand-800", label: "Task" },
  Decision: { icon: GitBranch, tone: "border-s-aegold-500", chip: "bg-aegold-100 text-aegold-800", label: "Decision" },
  Wait: { icon: Hourglass, tone: "border-s-camel-400", chip: "bg-camel-100 text-camel-800", label: "Wait" },
  End: { icon: Flag, tone: "border-s-aeblack-800", chip: "bg-aeblack-900 text-white", label: "End" },
  Automation: { icon: Zap, tone: "border-s-seablue-500", chip: "bg-seablue-100 text-seablue-800", label: "Automation" },
  Agent: { icon: Sparkles, tone: "border-s-seablue-600", chip: "bg-gradient-to-br from-aegold-500 to-seablue-600 text-white", label: "Agent" },
  SubProcess: { icon: GitFork, tone: "border-s-camel-600", chip: "bg-camel-200 text-camel-900", label: "Sub-process" },
};

function BuilderNodeView({ id, data, selected }: NodeProps<Node<BuilderNodeData>>) {
  const { node, issues, diff, ghost, heat, heatMax, sim, locale, roleLabel, agentName, editable, onRename, isStart } = data;
  const meta = KIND_META[node.kind] ?? KIND_META.Task;
  const Icon = meta.icon;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.label);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) input.current?.select(); }, [editing]);
  useEffect(() => { if (!editing) setDraft(node.label); }, [node.label, editing]);
  const label = locale === "ar" && node.labelAr ? node.labelAr : node.label;
  const errors = issues.filter((i) => i.severity === "Error");
  const warnings = issues.filter((i) => i.severity === "Warning");
  const primary = node.clocks.find((c) => c.primary) ?? node.clocks[0];
  const actions = (node.onEnter?.length ?? 0) + (node.onExit?.length ?? 0);
  const agents = node.agentHooks.length + (node.agent ? 1 : 0);
  const owner = node.owner ?? node.actors[0];
  const terminal = node.kind === "End";
  const heatShare = heat && heatMax ? heat / heatMax : 0;

  const commit = () => {
    setEditing(false);
    const text = draft.trim();
    if (text && text !== node.label) onRename?.(id, text);
  };

  return (
    <div
      className={cn(
        "group relative rounded-xl border border-s-4 bg-white px-3 py-2.5 text-start shadow-sand transition-[box-shadow,transform,opacity] duration-150",
        meta.tone,
        terminal && "rounded-full",
        selected && "ring-2 ring-aegold-400 ring-offset-2 ring-offset-sand-50",
        diff === "added" && "border-success-400 shadow-[0_0_0_4px_rgb(22_163_74/0.18)]",
        diff === "modified" && "border-warning-400 shadow-[0_0_0_4px_rgb(217_119_6/0.18)]",
        (diff === "removed" || ghost) && "border-dashed border-danger-300 bg-danger-50/60 opacity-60",
        sim === "current" && "ring-4 ring-aegold-300 animate-pulse-gold",
        sim === "visited" && "bg-success-50/60",
        heatShare > 0 && "shadow-[0_0_0_6px_var(--heat)]",
      )}
      style={{ width: NODE_W, ["--heat" as string]: `rgb(182 138 53 / ${0.12 + heatShare * 0.45})` }}
      dir={locale === "ar" ? "rtl" : "ltr"}
    >
      <Handle type="target" position={Position.Left} className="!size-3 !border-2 !border-white !bg-sand-400 transition group-hover:!bg-aegold-500" isConnectable={editable && !ghost} />
      {isStart ? <span className="absolute -top-2.5 start-3 rounded-full bg-success-600 px-2 py-px text-[9px] font-semibold uppercase tracking-wider text-white shadow">Start</span> : null}
      {diff === "added" ? <span className="absolute -top-2.5 end-3 rounded-full bg-success-600 px-2 py-px text-[9px] font-semibold uppercase tracking-wider text-white shadow">New</span> : null}
      {diff === "modified" ? <span className="absolute -top-2.5 end-3 rounded-full bg-warning-500 px-2 py-px text-[9px] font-semibold uppercase tracking-wider text-white shadow">Changed</span> : null}
      {diff === "removed" ? <span className="absolute -top-2.5 end-3 rounded-full bg-danger-600 px-2 py-px text-[9px] font-semibold uppercase tracking-wider text-white shadow">Removed</span> : null}
      {heat ? <span className="absolute -top-3 -end-3 grid min-w-7 place-items-center rounded-full bg-aegold-600 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white shadow-gold" title={`${heat} running`}>{heat}</span> : null}

      <div className="flex items-start gap-2">
        <span className={cn("mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg shadow-sm", meta.chip)}><Icon className="size-3.5" /></span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              ref={input}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(node.label); setEditing(false); } }}
              className="nodrag w-full rounded border border-aegold-300 bg-white px-1 text-[12.5px] font-semibold outline-none"
              dir="auto"
            />
          ) : (
            <p className="line-clamp-2 text-[12.5px] font-semibold leading-snug text-aeblack-950" onDoubleClick={() => editable && !ghost && setEditing(true)} title={editable ? "Double-click to rename" : undefined}>{label}</p>
          )}
          <p className="mt-0.5 truncate font-mono text-[10px] text-sand-600">{node.key}{owner ? <span className="font-sans"> · {roleLabel(owner)}{node.actors.length > 1 ? ` +${node.actors.length - 1}` : ""}</span> : null}</p>
        </div>
        {errors.length ? <XCircle className="size-4 shrink-0 text-danger-600" aria-label={errors.map((i) => i.message).join("\n")} /> : warnings.length ? <AlertTriangle className="size-4 shrink-0 text-warning-600" aria-label={warnings.map((i) => i.message).join("\n")} /> : null}
      </div>

      {!terminal ? (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {primary ? <Chip title={primary.legalBasis ?? primary.label}><Clock className="size-3" />{primary.days}{primary.workingDays ? "wd" : "d"}{node.clocks.length > 1 ? ` +${node.clocks.length - 1}` : ""}</Chip> : null}
          {node.kind === "Agent" && node.agent ? <Chip ai title={agentName(node.agent)}><Sparkles className="size-3" />{agentName(node.agent)}</Chip> : agents > 0 ? <Chip ai title={node.agentHooks.map(agentName).join(", ")}><Bot className="size-3" />{agents}</Chip> : null}
          {actions > 0 ? <Chip title="Actions"><Zap className="size-3" />{actions}</Chip> : null}
          {node.triggers?.some((t) => t.enabled && (t.kind === "schedule" || t.kind === "event")) ? <Chip title="Start triggers"><CalendarClock className="size-3" />{node.triggers.filter((t) => t.enabled).length}</Chip> : null}
          {node.kind === "SubProcess" && node.subProcess ? <Chip title="Playbook"><Workflow className="size-3" />{node.subProcess}</Chip> : null}
          {node.tasks.length > 0 ? <Chip title="Tasks"><Users className="size-3" />{node.tasks.length}</Chip> : null}
          {!node.visibleToApplicant ? <Chip title="Hidden from applicants"><EyeOff className="size-3" /></Chip> : null}
        </div>
      ) : null}
      <Handle type="source" position={Position.Right} className="!size-3 !border-2 !border-white !bg-sand-400 transition group-hover:!bg-aegold-500" isConnectable={editable && !ghost && !terminal} />
    </div>
  );
}

function Chip({ children, title, ai }: { children: React.ReactNode; title?: string; ai?: boolean }) {
  return <span title={title} className={cn("inline-flex max-w-[140px] items-center gap-1 truncate rounded-full px-1.5 py-0.5 text-[10px]", ai ? "bg-gradient-to-r from-aegold-100 to-seablue-100 text-aeblack-900" : "bg-sand-100 text-sand-800")}>{children}</span>;
}

export const BuilderNode = memo(BuilderNodeView);

/** A swimlane band behind the states (rendered in flow coordinates). */
export function LaneBand({ top, height, label, width, left, labelOffset }: { top: number; height: number; label: string; width: number; left: number; labelOffset: number }) {
  return (
    <div className="pointer-events-none absolute border-y border-dashed border-sand-200 bg-sand-100/40" style={{ transform: `translate(${left}px, ${top}px)`, width, height }}>
      <span className="absolute top-2 inline-block whitespace-nowrap rounded-md bg-white/85 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-sand-600 shadow-sm" style={{ left: labelOffset }}>{label}</span>
    </div>
  );
}
